import { engineComponent, Utils } from "neo-common";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { SceneHolderElement } from "../../CustomComponents/SceneHolderElement";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { ModuleManager } from "../../Managers/ModuleManager";
import { StageController } from "../../Components/StageController";
import { DataManager } from "../../Managers/DataManager";
import { Machine } from "../Machine";
import { Winframes } from "../Winframes";
import { LayoutSwitchElement } from "../../CustomComponents/LayoutSwitchElement";
import { LayoutManager } from "../../Managers/LayoutManager";
import { LayoutContainerElement } from "../../CustomComponents/LayoutContainerElement";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { FreespinsView } from "../Freespins/FreespinsView";
import { Tweens } from "../../Components/Effects/Tweens";
import { CustomUtils } from "../../Utils/CustomUtils";
import { CustomEase } from "../../Components/Effects/CustomEase";
import { Communication } from "neo-games";
import { ReelExtended } from "../../CustomComponents/ReelExtended";

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends LayoutContainerElement {
    public static LayoutManager: LayoutManager;
    public static Background: SwitchElement;
    public static PaylinesIndicator: SwitchElement;
    public static ExpandWildReel1: SwitchElement;
    public static ExpandWildReel2: SwitchElement;
    public static ExpandWildReel3: SwitchElement;
    public static ExpandWildReel4: SwitchElement;
    public static ExpandWildReel5: SwitchElement;
    private static readonly GambleInTransitionDuration: number = 30; //in frames

    public messages: LayoutSwitchElement;
    protected currentWins: Array<Communication.Game.IWin>;

    private _controller: BaseController;
    private _wasFinished: boolean = false;
    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;
    private _outroFinishDelay: FrameTimerEventEmitter;
    private _expandTimer: FrameTimerEventEmitter;
    private _copyTimer: FrameTimerEventEmitter;
    private _multiplyTimer: FrameTimerEventEmitter;
    private _fsMessageTimer: FrameTimerEventEmitter;
    private _fsMessageTimerLayout: FrameTimerEventEmitter;
    private _paylines: Paylines;
    private _paylinesNumbers: ContainerElement;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _winAnimator: WinAnimatorExtended;
    private _teasing: SwitchElement;
    private _background: SwitchElement;
    private _gambleTransition: SwitchElement;
    private _gambleTransitionSceneHolder: SceneHolderElement;
    private _fsTransition: SwitchElement;
    private _symbolsContainer: ContainerElement;
    private _noWinSymbolsContainer: ContainerElement;
    private _wildSymbolsContainer: ContainerElement;
    private _winframes: Winframes;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._outroFinishDelay = CreateFrameTimer("outroFinishDelay");
        this._outroFinishDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._expandTimer = CreateFrameTimer("expandTimer");
        this._expandTimer.on(FrameTimerEvents.Complete, this.machineStoppedAfterExpand.bind(this));

        this._copyTimer = CreateFrameTimer("copyTimer");
        this._copyTimer.on(FrameTimerEvents.Complete, this.machineStoppedAfterCopy.bind(this));

        this._multiplyTimer = CreateFrameTimer("multiplyTimer");
        this._multiplyTimer.on(FrameTimerEvents.Complete, this.machineStoppedDelayed.bind(this));

        this._fsMessageTimer = CreateFrameTimer("fsMessageDelay");
        this._fsMessageTimer.on(FrameTimerEvents.Complete, this.showFsMessage.bind(this));

        this._fsMessageTimerLayout = CreateFrameTimer("fsMessageDelayLayout");
        this._fsMessageTimerLayout.on(FrameTimerEvents.Complete, this.showFsMessageLayout.bind(this));

        StageController.baseStageContainer = this;
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        BaseView.LayoutManager = new LayoutManager();

        if (this.childs != null) {
            for (var key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                    this._paylines.reset();
                }

                if (key === "GambleTransition" && this.childs[key] instanceof SwitchElement) {
                    this._gambleTransition = this.childs[key];
                    this._gambleTransitionSceneHolder = <SceneHolderElement>(
                        this._gambleTransition.getChildElementByName("Scene")
                    );
                }

                if (key === "FreespinTransition" && this.childs[key] instanceof SwitchElement) {
                    this._fsTransition = this.childs[key];
                }

                if (key === "PaylinesNumbers" && this.childs[key] instanceof ContainerElement) {
                    this._paylinesNumbers = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinSymbolsContainer = this.childs[key];
                }

                if (key === "SymbolsContainer" && this.childs[key] instanceof ContainerElement) {
                    this._symbolsContainer = this.childs[key];
                }

                if (key === "WildSymbolsContainer" && this.childs[key] instanceof ContainerElement) {
                    this._wildSymbolsContainer = this.childs[key];
                }

                if (key === "PaylinesIndicator" && this.childs[key] instanceof SwitchElement) {
                    BaseView.PaylinesIndicator = this.childs[key];
                }

                if (key === "ExpandWildReel1" && this.childs[key] instanceof SwitchElement) {
                    BaseView.ExpandWildReel1 = this.childs[key];
                }

                if (key === "ExpandWildReel2" && this.childs[key] instanceof SwitchElement) {
                    BaseView.ExpandWildReel2 = this.childs[key];
                }

                if (key === "ExpandWildReel3" && this.childs[key] instanceof SwitchElement) {
                    BaseView.ExpandWildReel3 = this.childs[key];
                }

                if (key === "ExpandWildReel4" && this.childs[key] instanceof SwitchElement) {
                    BaseView.ExpandWildReel4 = this.childs[key];
                }

                if (key === "ExpandWildReel5" && this.childs[key] instanceof SwitchElement) {
                    BaseView.ExpandWildReel5 = this.childs[key];
                }

                if (key === "Teasing" && this.childs[key] instanceof SwitchElement) {
                    this._teasing = this.childs[key];
                }

                if (key === "Winframes" && this.childs[key] instanceof Winframes) {
                    this._winframes = this.childs[key];
                }

                if (key === "Background" && this.childs[key] instanceof SwitchElement) {
                    this._background = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (key === "Machine" && this.childs[key] instanceof MachineExtended) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimatorExtended) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "Messages" && this.childs[key] instanceof LayoutSwitchElement) {
                    this.messages = this.childs[key];
                }
            }

            BaseView.Background = CustomUtils.getObjectFrom("Background", this.childs, SwitchElement);

            if (this._symbols == null) {
                if (DEBUG) {
                    this.log("ERROR no symbols");
                }
            }

            if (this._machine == null) {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }

            if (this._paylines == null) {
                if (DEBUG) {
                    this.log("WARNING no paylines");
                }
            }

            if (this._winAnimator == null) {
                if (DEBUG) {
                    this.log("WARNING no winAnimator");
                }
            }
        }

        if (!Utils.isNullOrUndefined(this._paylinesNumbers)) {
            if (!Utils.isNullOrUndefined(this._paylines)) {
                this._paylines.addPaylinesNumbers(this._paylinesNumbers);
                this._paylines.reset();
            }
        }

        // Create own custom easing functions we used in game
        CustomEase._createOwnEasing();

        this.setupGambleTransitionSceneElement();
    }

    protected layoutChanged() {
        if (ModuleManager.freespinsEnabled()) {
            if (!ReelExtended.Tease) {
                this._fsMessageTimerLayout.start(0);
            }
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    const baseInitDataReels = GameManager.getBaseInitDataReels();
                    if (baseInitDataReels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(MachineExtended.NudgeStopped, this.onNudgeStopped.bind(this));
                        this._machine.on(MachineExtended.NudgeReelStopped, this.onNudgeReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStoppedBeforeNudge.bind(this));
                        this._machine.on(MachineExtended.NudgeMachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            baseInitDataReels,
                            this._teasing,
                            this.childs["Background"],
                        );
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.addElementInstance({ Winframes: this._winframes });
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.noWinExpandedSymbolsContainer =
                                this.childs["NoWinExpandedSymbolsContainer"];
                            this._winAnimator.expandedSymbolsContainer = this.childs["ExpandedSymbolsContainer"];
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this._noWinSymbolsContainer,
                                this._symbolsContainer,
                            );
                            this._winAnimator.wildSymbolsContainer = this.childs["WildSymbolsContainer"];
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);

                            if (CustomUtils.hasExpandWild(GameManager.baseResponse)) {
                                this._winAnimator.wildExpand_new(true);
                            }
                            if (CustomUtils.hasCopyWild(GameManager.baseResponse.customData)) {
                                this._winAnimator.wildCopy_new(true);
                            }
                            if (CustomUtils.hasMultiplyWild(GameManager.baseResponse)) {
                                this._winAnimator.wildMultiply_new(true);
                            }
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        if (this._gambleTransition != null) {
            this._gambleTransition.goToCase("HIDE_GAMBLE_INIT");
        }

        if (this._fsTransition != null) {
            this._fsTransition.goToCase("EMPTY");
        }

        if (BaseView.PaylinesIndicator != null) {
            BaseView.PaylinesIndicator.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel1 != null) {
            BaseView.ExpandWildReel1.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel2 != null) {
            BaseView.ExpandWildReel2.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel3 != null) {
            BaseView.ExpandWildReel3.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel4 != null) {
            BaseView.ExpandWildReel4.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel5 != null) {
            BaseView.ExpandWildReel5.goToCase("EMPTY");
        }
    }

    initIntro(): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;

        if (this._gambleTransition != null) {
            this._gambleTransition.goToCase("SHOW_INIT");
        }

        if (BaseView.PaylinesIndicator != null) {
            BaseView.PaylinesIndicator.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel1 != null) {
            BaseView.ExpandWildReel1.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel2 != null) {
            BaseView.ExpandWildReel2.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel3 != null) {
            BaseView.ExpandWildReel3.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel4 != null) {
            BaseView.ExpandWildReel4.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel5 != null) {
            BaseView.ExpandWildReel5.goToCase("EMPTY");
        }

        if (!Utils.isNullOrUndefined(BaseView.Background)) {
            BaseView.Background.goToCase("BASE");
        }

        if (ModuleManager.freespinsEnabled()) {
            PlayList.stop("SND_BaseLoop");
        } else if (GameManager.gameResponse.totalWin > 0) {
            //|| ModuleManager.freespinsEnabled()
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.loop("SND_BaseLoop");
        }

        if (ModuleManager.freespinsEnabled()) {
            if (this.messages != null) {
                this.messages.goToCase("SHOW_FS");
                this.showFsCount();
            }
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
    }

    collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop").resetVolume();

        if (BaseView.PaylinesIndicator != null) {
            BaseView.PaylinesIndicator.goToCase("EMPTY");
        }

        this._winAnimator.reset(true);
    }

    spin(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop").resetVolume();

        if (BaseView.PaylinesIndicator != null) {
            BaseView.PaylinesIndicator.goToCase("EMPTY");
        }

        const spinBitmask = this.calculateSpinBitmask();
        this._machine.spinStart(this._winAnimator.reset(true), true, this._winAnimator.currentSymbols, spinBitmask);
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    onNudgeStopped(): void {
        this._winAnimator.onNudgeStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    onNudgeReelStopped(reelIndex: number): void {
        this._winAnimator.onNudgeReelStopped(GameManager.baseResponse.screen.reels, reelIndex);
    }

    machineStoppedBeforeNudge(): void {
        if (CustomUtils.hasNudgeSymbols()) {
            this._machine.nudgeSpin();
            this._controller.machineStoppedBeforeNudge();
        } else {
            this.machineStopped();
        }
    }

    machineStopped(): void {
        if (CustomUtils.hasExpandWild(GameManager.baseResponse)) {
            let delay = this._winAnimator.wildExpand_new();
            this._expandTimer.start(delay);
        } else if (CustomUtils.hasCopyWild(GameManager.baseResponse.customData)) {
            let delay = this._winAnimator.wildCopy_new();
            this._copyTimer.start(delay);
        } else if (CustomUtils.hasMultiplyWild(GameManager.baseResponse)) {
            let delay = this._winAnimator.wildMultiply_new();
            this._multiplyTimer.start(delay);
        } else {
            this.machineStoppedDelayed();
        }
    }

    machineStoppedAfterExpand(): void {
        this._expandTimer.stop();

        if (CustomUtils.hasCopyWild(GameManager.baseResponse.customData)) {
            let delay = this._winAnimator.wildCopy_new();
            this._copyTimer.start(delay);
        } else if (CustomUtils.hasMultiplyWild(GameManager.baseResponse)) {
            let delay = this._winAnimator.wildMultiply_new();
            this._multiplyTimer.start(delay);
        } else {
            this.machineStoppedDelayed();
        }
    }

    machineStoppedAfterCopy(): void {
        this._copyTimer.stop();

        if (CustomUtils.hasMultiplyWild(GameManager.baseResponse)) {
            let delay = this._winAnimator.wildMultiply_new();
            this._multiplyTimer.start(delay);
        } else {
            this.machineStoppedDelayed();
        }
    }

    machineStoppedDelayed(): void {
        this._expandTimer.stop();
        this._copyTimer.stop();
        this._multiplyTimer.stop();

        this._wasFinished = false;
        this._controller.machineStopped();
        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.setVolume("SND_BaseLoop", 0);
        }
    }

    hasMultiplyWildWithWin(wins: Array<Communication.Game.IWin>): boolean {
        this.currentWins = wins;
        let hasCashWin = CustomUtils.hasCashWin(this.currentWins);
        const customData = GameManager.baseResponse.customData;
        if (!Utils.isNullOrUndefined(customData)) {
            if (hasCashWin) {
                for (let i = 0; i < this.currentWins.length; i++) {
                    if (this.currentWins[i].symbols.indexOf(CustomUtils.SymbolWildMultiply) > -1) {
                        if (customData.hasOwnProperty("wildSymbols")) {
                            for (let j: number = 0; j < customData.wildSymbols.length; j++) {
                                if (customData.wildSymbols[j].type == "multiplier") {
                                    return true;
                                }
                            }
                        }
                    }
                }
            }
        }

        return false;
    }

    finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay || ModuleManager.freespinsEnabled()) {
            PlayList.setVolume("SND_BaseLoop", 0);

            if (ModuleManager.freespinsEnabled()) {
                // PlayList.loop('SND_FSWait');
            } else {
                PlayList.loop("SND_BaseRecapLoop");
            }
        }

        if (this.messages != null) {
            if (ModuleManager.freespinsEnabled()) {
                this._fsMessageTimer.start(60);
            }
        }

        this._controller.allWinsFinished();
    }

    public showFsMessage(): void {
        this._fsMessageTimer.stop();

        PlayList.play("SND_FSWin");
        PlayList.loop("SND_FSWait", 120);

        if (this.messages != null) {
            this.messages.goToCase("SHOW_FS");
        }

        this.showFsCount();
    }

    public showFsMessageLayout(): void {
        this._fsMessageTimerLayout.stop();

        PlayList.loop("SND_FSWait", 0);
        if (this.messages != null) {
            this.messages.goToCase("SHOW_FS");
        }

        this.showFsCount();
    }

    public showFsCount(): void {
        const totalCount = GameManager.freespinsResponse.freeSpins.totalCount;
        const bonusWinCount = this.messages.getChildElementByName("Number" + totalCount) as ImageElement;
        if (!Utils.isNullOrUndefined(bonusWinCount)) {
            bonusWinCount.visible = true;

            if (bonusWinCount.alpha < 1) {
                Tweens.removeTweens(bonusWinCount, "all");
                Tweens.customTween(bonusWinCount, {
                    tween: "alpha",
                    tweenValues: [bonusWinCount.alpha, 1],
                    tweenDuration: [10],
                    tweenLoop: false,
                });
            }
        }

        ReelExtended.Tease = false;
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false): void {
        StageController.baseStage = true;

        if (!Utils.isNullOrUndefined(BaseView.Background)) {
            BaseView.Background.goToCase("BASE");
        }

        if (BaseView.ExpandWildReel1 != null) {
            BaseView.ExpandWildReel1.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel2 != null) {
            BaseView.ExpandWildReel2.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel3 != null) {
            BaseView.ExpandWildReel3.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel4 != null) {
            BaseView.ExpandWildReel4.goToCase("EMPTY");
        }

        if (BaseView.ExpandWildReel5 != null) {
            BaseView.ExpandWildReel5.goToCase("EMPTY");
        }

        this.allSwitchElementToCase("NORMAL");

        if (GameManager.gameResponse.totalWin > 0) {
            //fs return
            PlayList.setVolume("SND_BaseLoop", 0);
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.loop("SND_BaseLoop").resetVolume();
        }

        if (long) {
            if (FreespinsView.Transition != null) {
                this.addChild(FreespinsView.Transition);
            }
            //this._introDelay.start(0);
            if (BaseView.PaylinesIndicator != null) {
                BaseView.PaylinesIndicator.goToCase("INIT");
            }
            this.introFinished();
        } else {
            if (BaseView.PaylinesIndicator != null) {
                BaseView.PaylinesIndicator.goToCase("INIT");
            }
            this.introFinished();
        }
    }

    introFinished(): void {
        if (this._gambleTransition != null) {
            this._gambleTransition.setDynamicTime(0);
            this._gambleTransition.goToCase("SHOW");
        }

        if (this._fsTransition != null) {
            this._fsTransition.setDynamicTime(0);
            this._fsTransition.goToCase("SHOW");
        }

        if (ModuleManager.freespinsEnabled()) {
            if (this.messages != null) {
                this.messages.goToCase("SHOW_FS");
            }
        }

        this._controller.introFinished();
    }

    outro(): void {
        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_BaseRecapLoop");

        let wasFinished: boolean = this._wasFinished;
        let inHighWins: boolean = this._winAnimator.reset(true);

        if (ModuleManager.freespinsActive()) {
            if (FreespinsView.Transition != null) {
                this.addChild(FreespinsView.Transition);
                FreespinsView.Transition.goToCase("IN");

                PlayList.stop("SND_FSWait");
                PlayList.play("SND_FSTransIn");
            }

            /*if (!Utils.isNullOrUndefined(BaseView.Background)) {
                BaseView.Background.goToCase('BASE_TO_FS');
            }*/

            if (
                !Utils.isNullOrUndefined(this.messages) != null &&
                (this.messages.getCurrentCase() == "SHOW_FS" || this.messages.getCurrentCase() == "SHOW_FS_IDLE")
            ) {
                this.messages.goToCase("HIDE_FS_BASE");
            }

            this._outroDelay.start(15);
        } else {
            PlayList.play("SND_GambleTransition");

            if (!Utils.isNullOrUndefined(BaseView.Background)) {
                BaseView.Background.goToCase("OUTRO");
            }

            if (wasFinished) {
                this.outroStart();
            } else {
                if (inHighWins) {
                    this._outroDelay.start(60);
                    this._gambleTransition.setDynamicTime(0);
                    this._gambleTransition.goToCase("HIDE_GAMBLE");
                } else {
                    this._outroDelay.start(30);
                    this._gambleTransition.setDynamicTime(0);
                    this._gambleTransition.goToCase("HIDE_GAMBLE");
                }
            }
        }
    }

    outroStart(): void {
        if (ModuleManager.freespinsActive()) {
            // this.allSwitchElementToCase("EMPTY");
            this.outroFinished();
        } else {
            this._gambleTransition.setDynamicTime(0);
            this._gambleTransition.goToCase("HIDE_GAMBLE");
            this._outroFinishDelay.start(30);
        }
    }

    outroFinished(): void {
        // this.allSwitchElementToCase("EMPTY");
        this._controller.outroFinished();
        this._winAnimator.resetTimer();
    }

    private allSwitchElementToCase(caseType: string): void {
        for (let key in this.childs) {
            if (this.childs[key] instanceof SwitchElement) {
                let elem: SwitchElement = this.childs[key];

                if (elem.hasCase(caseType)) {
                    elem.goToCase(caseType);
                }
            }
        }
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecapLoop");
        }
    }

    public calculateSpinBitmask(): number {
        // return (1 << this._winAnimator.reelCount) - 1;
        return 0b11111;
    }

    private setupGambleTransitionSceneElement(): void {
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["PaylinesIndicator"],
            parent: this,
            index: this.getChildIndex(this.childs["PaylinesIndicator"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["PaylinesNumbers"],
            parent: this,
            index: this.getChildIndex(this.childs["PaylinesNumbers"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["ReelsBody"],
            parent: this,
            index: this.getChildIndex(this.childs["ReelsBody"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["NoWinContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["NoWinContainer"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["NoWinExpandedSymbolsContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["NoWinExpandedSymbolsContainer"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Paylines"],
            parent: this,
            index: this.getChildIndex(this.childs["Paylines"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["ExpandedSymbolsContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["ExpandedSymbolsContainer"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["SymbolsContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["SymbolsContainer"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["ExpandWildReel1"],
            parent: this,
            index: this.getChildIndex(this.childs["ExpandWildReel1"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["ExpandWildReel2"],
            parent: this,
            index: this.getChildIndex(this.childs["ExpandWildReel2"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["ExpandWildReel3"],
            parent: this,
            index: this.getChildIndex(this.childs["ExpandWildReel3"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["ExpandWildReel4"],
            parent: this,
            index: this.getChildIndex(this.childs["ExpandWildReel4"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["ExpandWildReel5"],
            parent: this,
            index: this.getChildIndex(this.childs["ExpandWildReel5"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Winframes"],
            parent: this,
            index: this.getChildIndex(this.childs["Winframes"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["WildSymbolsContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["WildSymbolsContainer"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Teasing"],
            parent: this,
            index: this.getChildIndex(this.childs["Teasing"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Logo"],
            parent: this,
            index: this.getChildIndex(this.childs["Logo"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Overlay"],
            parent: this,
            index: this.getChildIndex(this.childs["Overlay"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["WinAnimator"],
            parent: this,
            index: this.getChildIndex(this.childs["WinAnimator"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Messages"],
            parent: this,
            index: this.getChildIndex(this.childs["Messages"]),
        });
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/